import { useEffect, useRef } from 'react'

// Cloudflare Turnstile, rendered explicitly so the widget only exists while a
// modal that can submit a token is on screen.
//
// Two states, both of which must work:
//  - NO site key (VITE_TURNSTILE_SITE_KEY unset, which is every build until
//    Keith sets it): render nothing, report an undefined token, and let the
//    caller send its request without a captchaToken. Supabase CAPTCHA
//    enforcement is deliberately OFF this release, so an undefined token
//    costs nothing.
//  - Site key set: inject the script once per page, render the widget, and
//    report the token. Expiry, widget error and a failed script load all
//    report undefined rather than hanging - once enforcement is on the
//    server rejects the tokenless request and the modal renders that error,
//    which is a better outcome than a spinner with no end.
//
// Tokens are SINGLE USE. The caller bumps resetNonce after every submit
// attempt, success or failure; that resets the widget and clears the token so
// a consumed one is never resubmitted on a retry.

type TurnstileOptions = {
  sitekey: string
  callback: (token: string) => void
  'expired-callback': () => void
  'error-callback': () => void
}

type TurnstileApi = {
  render: (el: HTMLElement, options: TurnstileOptions) => string
  reset: (widgetId?: string) => void
  remove?: (widgetId?: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const SITE_KEY: string | undefined = import.meta.env.VITE_TURNSTILE_SITE_KEY
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

// Module-level guard: the script is injected once per page even if the widget
// mounts and unmounts repeatedly (open modal, close it, open it again).
let scriptLoading: Promise<boolean> | null = null

function loadTurnstileScript(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false)
  if (window.turnstile) return Promise.resolve(true)
  if (scriptLoading) return scriptLoading
  scriptLoading = new Promise<boolean>(resolve => {
    // eslint-disable-next-line i18next/no-literal-string -- DOM tag name
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    // A load FAILURE must not be cached. Keeping the settled promise around
    // meant one transient CDN blip bricked the captcha for the whole page
    // life, so drop the guard and the dead node and let a later mount retry.
    const giveUp = () => {
      scriptLoading = null
      script.remove()
      resolve(false)
    }
    script.onload = () => {
      if (window.turnstile) resolve(true)
      else giveUp()
    }
    script.onerror = giveUp
    document.head.appendChild(script)
  })
  return scriptLoading
}

export default function TurnstileWidget({
  onToken,
  resetNonce,
}: {
  onToken: (token: string | undefined) => void
  resetNonce: number
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const initialNonceRef = useRef(resetNonce)

  // No site key configured: report undefined once so a caller that waits on a
  // token is never left waiting.
  useEffect(() => {
    if (!SITE_KEY) onToken(undefined)
  }, [onToken])

  useEffect(() => {
    if (!SITE_KEY) return
    let cancelled = false
    void loadTurnstileScript().then(ok => {
      if (cancelled) return
      const api = window.turnstile
      if (!ok || !api) {
        onToken(undefined)
        return
      }
      if (!containerRef.current || widgetIdRef.current !== null) return
      widgetIdRef.current = api.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token: string) => onToken(token),
        'expired-callback': () => onToken(undefined),
        'error-callback': () => onToken(undefined),
      })
    })
    return () => { cancelled = true }
  }, [onToken])

  // Single-use token discipline. Skipped on the first render so a fresh
  // widget is not reset before it has issued anything.
  useEffect(() => {
    if (resetNonce === initialNonceRef.current) return
    onToken(undefined)
    const api = window.turnstile
    if (api && widgetIdRef.current !== null) api.reset(widgetIdRef.current)
  }, [resetNonce, onToken])

  useEffect(() => () => {
    const api = window.turnstile
    if (api?.remove && widgetIdRef.current !== null) api.remove(widgetIdRef.current)
    widgetIdRef.current = null
  }, [])

  if (!SITE_KEY) return null
  return <div ref={containerRef} data-testid="turnstile-widget" className="flex justify-center" />
}
